import { Injectable } from '@angular/core';

import { BetSlipType, KeyValueInformation } from '@bpos';
import { PromoTokenType, PromotionToken, SignPostType } from '@bpos/v1/my-bets';
import { RewardTargetType } from '@bpos/v1/sports-promo/tokens';
import { ParticipantMarketType } from '@cds/betting-offer';
import { TaxationConfig, UKGCConfig } from '@frontend/sports/common/client-config-data-access';
import { Nullable } from '@frontend/sports/common/core/utils/extended-types';
import { IConfigSettings, SyncUserSettingsService } from '@frontend/sports/user/feature';
import { Decimal } from 'decimal.js';
import { PayoutType, RewardTokenType } from 'packages/sports/web/app/src/tokens-base/token-base.models';
import { TokensSummaryService } from 'packages/sports/web/app/src/tokens-base/tokens-summary.service';

import { BetslipBetBuilderPick } from '../../../core/picks/betslip-bet-builder-pick';
import { V1PickId } from '../../../core/picks/pick-id';
import { PickType, PriceType } from '../../../core/picks/pick-models';
import { ISucceededSlipResult, SlipResult, SlipResultStatus } from '../../betplacement/models';
import { IBetslipResultSummaryViewModel } from '../../result/models/betslip-result-summary.model';
import { UsedTokenPayoutSummaryService } from '../../result/services/used-token-payout-summary.service';
import { getTokenWinningsBoost } from '../../result/utils/result-tokens.utils';
import { convertPayoutType, convertTokenType } from '../../reward-tokens/helpers/token-types.helper';
import { BetAndGetEdsToken, BetAndGetToken, PromoTokenPlaceBetSignPostingInfo, RiskFreeToken } from '../../reward-tokens/reward-tokens.model';
import BetslipTaxationService from '../../taxation/services/betslip-taxation.service';
import { IQuickBetPlaceResultState } from '../quick-bet.state';

export type QuickBetBetslipResultSummaryViewModel = Nullable<{
    summary: IBetslipResultSummaryViewModel;
    startingPrice: { hasStartingPrice: boolean; startingPriceTitle?: string };
}>;

@Injectable()
export class QuickBetResultSlipProcessorService {
    constructor(
        private tokensSummaryService: TokensSummaryService,
        private userSettings: SyncUserSettingsService,
        private betslipTaxationService: BetslipTaxationService,
        private taxationConfig: TaxationConfig,
        private uKGCConfig: UKGCConfig,
        private usedTokenPayoutSummaryService: UsedTokenPayoutSummaryService,
    ) {}

    buildSummaryViewModel(quickbetResultState: IQuickBetPlaceResultState, config: Readonly<IConfigSettings>): QuickBetBetslipResultSummaryViewModel {
        const succeededSlip = this.isSucceededSlip(quickbetResultState.slipResults) ? quickbetResultState.slipResults : null;
        if (!succeededSlip) {
            return null;
        }
        const summary = quickbetResultState.summary;
        let hasStartingPrice = false;
        let hasXCastPick = false;
        const fixtureIds: string[] = [];

        succeededSlip.picks.forEach((sp) => {
            if (sp.pickId.getPickType() === PickType.V1Pick) {
                fixtureIds.push((sp.pickId as V1PickId)['eventId']);
            } else if (sp.pickId.getPickType() === PickType.BetBuilderPick || BetslipBetBuilderPick.isPick(sp.clientPick!)) {
                if (sp.clientPick) {
                    fixtureIds.push((sp.clientPick as BetslipBetBuilderPick).eventId);
                }
            } else {
                fixtureIds.push(sp.pickId.fixtureId);
            }
            if (
                !!sp.clientPick?.market &&
                (sp.clientPick?.market.marketType === ParticipantMarketType.Forecast ||
                    sp.clientPick?.market.marketType === ParticipantMarketType.Tricast)
            ) {
                hasXCastPick = true;
            }
            if (sp.clientPick?.priceType === PriceType.StartingPrice) {
                hasStartingPrice = true;
            }
        });

        const duplicateFixtureIds = fixtureIds.filter((currentValue: any, currentIndex: any) => fixtureIds.indexOf(currentValue) !== currentIndex);
        const taxation = summary.possibleWinningsGross ? summary.possibleWinningsGross - summary.possibleWinningsNet : 0;
        const possibleAmount =
            this.userSettings.isTurnoverTaxApplied && succeededSlip.isFreebet && !!summary.possibleWinningsGross
                ? summary.possibleWinningsGross + summary.stake
                : summary.possibleWinningsGross;
        const taxRate = this.betslipTaxationService.getTaxRate(possibleAmount ? new Decimal(possibleAmount) : null);
        const data: IBetslipResultSummaryViewModel = {
            currency: summary.currency ?? '',
            numberOfBets: { title: '', value: summary.numberOfBets },
            possibleWinnings: {
                title: '',
                value: summary.possibleWinningsGross ? new Decimal(summary.possibleWinningsGross).minus(new Decimal(taxation)) || null : null,
            },
            stake: { title: '', value: summary.stake },
            freebetStake: 0,
            isPotentailReturn: !!(
                this.uKGCConfig.isPotentialReturnsEnabledForSingle &&
                succeededSlip.betType === BetSlipType.Single &&
                duplicateFixtureIds.length &&
                !hasXCastPick &&
                !hasStartingPrice
            ),

            usedRewardTokens: [],
            tokenPayoutSummaries: [],
        };
        const isPlaceholderTax = !taxation && this.isServiceTaxationEnabled(succeededSlip);

        if (succeededSlip.totalOdds || succeededSlip.picks[0].totalOdds) {
            data.odds = {
                title: '',
                value: succeededSlip.totalOdds ?? succeededSlip.picks[0].totalOdds, // If we have Each Way then the total odds is coming from the picks
                boosted: quickbetResultState.boostedOdds ?? undefined,
            };
        }
        if (taxation > 0 || isPlaceholderTax) {
            if (this.userSettings.isTurnoverTaxApplied && succeededSlip.isFreebet) {
                if (taxRate.equals(0) || !summary.possibleWinningsGross) {
                    data.taxation = undefined;
                } else {
                    const freebetTotalPossibleWinningsGross = summary.possibleWinningsGross + summary.stake;
                    data.taxation = {
                        possibleWinningsGrossTitle: '',
                        possibleWinningsGross: freebetTotalPossibleWinningsGross ? new Decimal(freebetTotalPossibleWinningsGross) || null : null,
                        taxationValue: {
                            amount: new Decimal(freebetTotalPossibleWinningsGross * taxRate.toNumber()),
                            rate: taxRate.greaterThan(0) ? taxRate : new Decimal(0),
                            isPlaceholderTax,
                        },
                    };
                }
            } else {
                data.taxation = {
                    possibleWinningsGrossTitle: '',
                    possibleWinningsGross: summary.possibleWinningsGross ? new Decimal(summary.possibleWinningsGross) || null : null,
                    taxationValue: { amount: new Decimal(taxation), rate: taxRate.greaterThan(0) ? taxRate : new Decimal(0), isPlaceholderTax },
                };
            }
        }

        this.addTokensData(data, succeededSlip);

        const quickBetBetslipResultSummaryView: QuickBetBetslipResultSummaryViewModel = { summary: data, startingPrice: { hasStartingPrice } };

        return quickBetBetslipResultSummaryView;
    }

    private isServiceTaxationEnabled(succeededSlips: ISucceededSlipResult): boolean {
        if (!this.taxationConfig.isTaxationEnabled) {
            return false;
        }

        return succeededSlips.picks.some((p) => (p.clientPick ? this.taxationConfig.taxableSports.includes(p.clientPick?.sportId) : false));
    }

    private addTokensData(data: IBetslipResultSummaryViewModel, slip: ISucceededSlipResult): void {
        if (slip.isFreebet) {
            data.usedRewardTokens.push({ rewardTargetType: RewardTargetType.NonWelcomeOffer, tokenType: RewardTokenType.FreeBet });
            data.freebetStake = data.freebetStake + (slip.stake ?? 0);

            return;
        }

        if (slip.promoTokens.length > 0) {
            this.addPromoTokensData(data, slip);

            return;
        }

        if (!slip.edsPromoTokens.length) {
            return;
        }

        this.addBetAndGetSummary(data, slip);
    }

    private addPromoTokensData(data: IBetslipResultSummaryViewModel, slip: ISucceededSlipResult): void {
        const token = slip.promoTokens[0];
        const placeBetSignPostData =
            this.usedTokenPayoutSummaryService.getPlaceBetSignPostingDetails<PromoTokenPlaceBetSignPostingInfo>(slip.signPostings) ?? {};
        placeBetSignPostData.rewardTargetType = placeBetSignPostData.rewardTargetType ?? RewardTargetType.NonWelcomeOffer;
        const tokenType = convertTokenType(token.tokenType);

        if (tokenType) {
            data.usedRewardTokens.push({ rewardTargetType: placeBetSignPostData.rewardTargetType, tokenType });
        }

        this.addTokensPayoutSummary(data, token, slip, placeBetSignPostData);
    }

    private addTokensPayoutSummary(
        data: IBetslipResultSummaryViewModel,
        token: PromotionToken,
        slip: ISucceededSlipResult,
        placeBetSignPostData: PromoTokenPlaceBetSignPostingInfo,
    ): void {
        if (token.tokenType === PromoTokenType.RiskFree) {
            const riskFreePayoutSummary = this.getRiskFreePayoutSummary(token, slip, placeBetSignPostData);

            data.tokenPayoutSummaries.push({ summary: riskFreePayoutSummary, betNumber: slip.betNumber });

            return;
        }

        if (data.possibleWinnings.value?.toNumber()! > 0 && token.tokenType === PromoTokenType.OddsBoost) {
            this.addOddsBoostPayoutSummary(data, token, slip);

            return;
        }

        if (data.possibleWinnings.value?.toNumber()! > 0 && token.tokenType === PromoTokenType.AccaBoost) {
            const boostedWinnings = token.additionalInformation.informationItems.find((item) => item.key === 'BoostedWinnings')?.value ?? '0';

            data.possibleWinnings.boostedValue = new Decimal(boostedWinnings).add(data.possibleWinnings.boostedValue ?? 0);

            return;
        }
    }

    private getRiskFreePayoutSummary(
        token: PromotionToken,
        slip: ISucceededSlipResult,
        placeBetSignPostData: PromoTokenPlaceBetSignPostingInfo,
    ): string {
        const riskFreebetToken: Partial<RiskFreeToken> = {
            tokenType: RewardTokenType.RiskFreeBet,
            payoutType: (<unknown>token.payoutType) as PayoutType,
            maximumPayout: parseFloat(
                token.additionalInformation.informationItems.find((item) => item.key === 'RiskFreeMaxCompensation')?.value ?? '0',
            ),
            riskFreePercentage: parseInt(
                token.additionalInformation.informationItems.find((item) => item.key === 'RiskFreePercentage')?.value ?? '0',
                10,
            ),
        };

        return this.tokensSummaryService.getRiskFreeSummary(
            riskFreebetToken.payoutType!,
            riskFreebetToken.maximumPayout!,
            riskFreebetToken.riskFreePercentage,
            slip.stake,
            placeBetSignPostData.noOfSlabs,
            placeBetSignPostData.slabAmount,
        );
    }

    private addOddsBoostPayoutSummary(data: IBetslipResultSummaryViewModel, token: PromotionToken, slip: ISucceededSlipResult): void {
        const winningBoost = getTokenWinningsBoost(token);
        data.possibleWinnings.value = data.possibleWinnings.value!.add(winningBoost);

        const payoutType = convertPayoutType(token.payoutType) || PayoutType.Bonus;

        const summary = this.tokensSummaryService.getBoostedWinningsSummary(payoutType, winningBoost);

        data.tokenPayoutSummaries.push({ summary, betNumber: slip.betNumber });
    }

    private addBetAndGetSummary(data: IBetslipResultSummaryViewModel, slip: ISucceededSlipResult): void {
        if (!slip.signPostings.length) {
            const placeBetSignPost = slip.edsPromoTokens[0].additionalInformation.informationItems.find(
                (item: KeyValueInformation) => item.key === 'PlaceBetSignPostingData',
            )?.value;

            const placeBetSignPostVersion = Number(
                slip.edsPromoTokens[0].additionalInformation.informationItems.find(
                    (item: KeyValueInformation) => item.key === 'PlaceBetSignPostingVersion',
                )?.value ?? 1,
            );

            slip.signPostings = placeBetSignPost
                ? [{ type: SignPostType.BetPlacement, version: placeBetSignPostVersion, data: placeBetSignPost }]
                : [];
        }

        const betAndGetSignpostData = this.usedTokenPayoutSummaryService.getPlaceBetSignPostingDetails<BetAndGetEdsToken>(slip.signPostings);

        if (!betAndGetSignpostData) {
            return;
        }

        const rewardTargetType = betAndGetSignpostData.rewardTargetType ?? RewardTargetType.NonWelcomeOffer;

        const betAndGetToken = {
            payoutType: betAndGetSignpostData.payoutType,
            rewardTriggerType: betAndGetSignpostData.triggerType,
            payoutValue: betAndGetSignpostData.payoutDetails.value,
            percentagePayout: betAndGetSignpostData.payoutDetails.percentage,
            maximumPayout: betAndGetSignpostData.payoutDetails.maxValue,
        } as BetAndGetToken;

        const betAndGetSummary = this.tokensSummaryService.getBetAndGetSummary(betAndGetToken);

        data.tokenPayoutSummaries.push({ summary: betAndGetSummary, betNumber: slip.betNumber });
        data.usedRewardTokens.push({ rewardTargetType, tokenType: RewardTokenType.BetAndGet });
    }

    private isSucceededSlip(resultSlip: SlipResult): resultSlip is ISucceededSlipResult {
        return resultSlip.status === SlipResultStatus.Success;
    }
}
